import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';

import { useMe } from '../auth/context';
import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { BrokerProfileSection } from '../documents/BrokerProfileSection';
import { AppointmentGoalSection } from './AppointmentGoalSection';
import { BrokerContractSection } from '../pricing/BrokerContractSection';
import { MandatesSection } from '../pricing/MandatesSection';
import { PageLoader } from '../components/Loader';
import { addDays, appointmentStatusLabels, fmtDateTime, fromLocal, localDate } from '../ops/format';
import { RoutePanel } from '../ops/RoutePanel';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { TicketList } from '../ops/TicketCards';

const n = new Intl.NumberFormat('de-DE');

function Stat({ value, label, to, tone }: { value: number; label: string; to?: string; tone?: 'warn' }) {
  const body = (
    <>
      <span className={tone && value > 0 ? `stat-value stat-${tone}` : 'stat-value'}>{n.format(value)}</span>
      <span className="stat-label">{label}</span>
    </>
  );
  return <li className="stat">{to ? <Link to={to}>{body}</Link> : body}</li>;
}

// MaklerPage: alles zu einem Makler an einer Stelle – Gebiete, Leads, heutige Route, nächste Termine und
// offene Rückfragen. Aufgaben stehen hier im Kontext des Maklers, nicht in einer eigenen Liste.
export function MaklerPage() {
  const { userId = '' } = useParams();
  const { project } = useProject();
  const papi = useProjectApi();
  const me = useMe();
  const base = `/p/${project.key}`;
  const team = useQuery({ queryKey: projectKeys.team(project.id), queryFn: ({ signal }) => papi.teamOverview(signal) });
  const today = localDate();
  const q = { from: fromLocal(today, '00:00'), to: fromLocal(addDays(today, 14), '00:00'), user_id: userId };
  const upcoming = useQuery({ queryKey: projectKeys.calendar(project.id, q), queryFn: ({ signal }) => papi.calendar(q, signal) });
  const route = useQuery({ queryKey: projectKeys.route(project.id, today, userId), queryFn: ({ signal }) => papi.fieldSalesRoute(today, userId, signal) });

  if (team.isPending) return <PageLoader />;
  if (team.isError) return <ErrorMessage error={team.error} onRetry={() => void team.refetch()} />;
  const m = team.data.field_sales.find((f) => f.user_id === userId);
  if (!m)
    return (
      <div className="page">
        <EmptyState title="Makler nicht gefunden">Dieser Benutzer ist in diesem Projekt kein aktiver Makler.</EmptyState>
      </div>
    );
  const items = (upcoming.data?.items ?? []).filter((a) => a.status !== 'CANCELED');

  return (
    <div className="page">
      <header className="page-header">
        <h1>{m.name}</h1>
        <Link className="btn" to={`${base}/gebiete`}>
          Alle Gebiete & Makler
        </Link>
      </header>
      <p className="muted">
        {m.areas.length ? `Gebiet${m.areas.length > 1 ? 'e' : ''}: ${m.areas.join(', ')}` : 'Noch kein Gebiet zugeordnet.'} · Outlook{' '}
        {m.outlook_connected ? 'verbunden' : 'nicht verbunden'}
      </p>
      <section className="card" aria-label="Kennzahlen">
        <ul className="stats">
          <Stat value={m.leads} label="Leads" to={`${base}/leads?assigned_field_sales_user_id=${m.user_id}`} />
          <Stat value={m.new_leads} label="noch nicht angerufen" to={`${base}/leads?assigned_field_sales_user_id=${m.user_id}&status=NEW`} />
          <Stat value={m.appointments_today} label="Termine heute" />
          <Stat value={m.appointments_next_7_days} label="Termine (7 Tage)" to={`${base}/termine?ansicht=kalender&user=${m.user_id}`} />
          <Stat value={m.appointments_last_30_days} label="Termine (30 Tage)" />
          <Stat
            value={m.remote_appointments_last_30_days ?? 0}
            label="Telefon-/Videotermine (30 Tage)"
            tone={m.appointment_goal === 'REMOTE' ? undefined : 'warn'}
          />
          <Stat value={m.open_tickets} label="offene Rückfragen" tone="warn" to={`${base}/posteingang?ansicht=rueckfragen&makler=${m.user_id}`} />
        </ul>
      </section>
      <AppointmentGoalSection
        userId={m.user_id}
        goal={m.appointment_goal ?? 'ON_SITE'}
        explicit={!!m.appointment_goal_set}
        remote30={m.remote_appointments_last_30_days ?? 0}
      />
      {route.data && route.data.stops.length > 0 && (
        <section className="card" aria-labelledby="route-title">
          <h2 id="route-title">Heute</h2>
          <RoutePanel route={route.data} />
        </section>
      )}
      <section className="card" aria-labelledby="next-title">
        <h2 id="next-title">Nächste Termine (14 Tage)</h2>
        {upcoming.isPending ? (
          <PageLoader />
        ) : items.length === 0 ? (
          <p className="muted">Keine Termine in den nächsten 14 Tagen.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Termin</th>
                  <th>Lead</th>
                  <th>Ort</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`${base}/termine/${a.id}`}>{fmtDateTime(a.start_at)}</Link>
                    </td>
                    <td>{a.lead_name ?? a.title}</td>
                    <td>{a.city ?? '–'}</td>
                    <td>{appointmentStatusLabels[a.status]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <BrokerContractSection userId={m.user_id} name={m.name} />
      <MandatesSection userId={m.user_id} />
      <section className="card" aria-labelledby="tickets-title">
        <h2 id="tickets-title">Offene Rückfragen</h2>
        <TicketList q={{ status: ['open'], assigned: 'all', field_sales_user_id: m.user_id, sort: 'priority' }} />
      </section>
      {me.global_permissions.includes('user.manage') && <BrokerProfileSection userId={m.user_id} title={`Partnerprofil von ${m.name} (Exposé und Dokumente)`} />}
    </div>
  );
}
